import React from 'react';
import { siteConfig } from '../lib/config';

// ─── Types ──────────────────────────────────────────────────────────

export interface LivestreamLogEntry {
  direction: 'SEND' | 'RECV' | 'INFO' | 'ERROR';
  timestamp: string;
  cmd: number | string;
  label: string;
  raw: unknown;
}

export interface DiceResult {
  dice: number[];
  total: number;
  taiXiu: 'TÀI' | 'XỈU';
  chanLe: 'CHẴN' | 'LẺ';
}

export interface HistoryEntry {
  sessionId: number;
  result: DiceResult;
  resultRaw: string;
}

export interface SessionState {
  sessionId: number | null;
  countdown: number;
  timeBet: number;
  endBettingTime: number | null;
  stopBetSecond: number;
  status: string;
  coins: number[];
  jackpot: number;
  dealerName: string | null;
  odds: Array<{ name: string; rate: number }> | null;
  betSummary: Array<{ eid: string; v: number; uC: number }> | null;
  totalPlayers: number | null;
  lastResult: DiceResult | null;
  betTypeResult: string | null;
  history: HistoryEntry[];
}

export type StreamStatus = 'idle' | 'connecting' | 'running' | 'done' | 'failed';

// ─── Helpers ────────────────────────────────────────────────────────

export function parseResult(resultRaw: string): DiceResult {
  const dice = resultRaw.split('').map(Number);
  const total = dice.reduce((a, b) => a + b, 0);
  return {
    dice,
    total,
    taiXiu: total >= 11 ? 'TÀI' : 'XỈU',
    chanLe: total % 2 === 0 ? 'CHẴN' : 'LẺ',
  };
}

export function formatCoin(value: number): string {
  if (value >= 1000000) return `${value / 1000000}M`;
  if (value >= 1000) return `${value / 1000}K`;
  return value.toString();
}

export function formatJackpot(value: number): string {
  return value.toLocaleString('vi-VN');
}

const INITIAL_SESSION: SessionState = {
  sessionId: null,
  countdown: 0,
  timeBet: 35,
  endBettingTime: null,
  stopBetSecond: 3,
  status: '',
  coins: [],
  jackpot: 0,
  dealerName: null,
  odds: null,
  betSummary: null,
  totalPlayers: null,
  lastResult: null,
  betTypeResult: null,
  history: [],
};

// ─── Hook ───────────────────────────────────────────────────────────

/**
 * useLivestreamSession — quản lý SSE stream + parse session data cho 1 account.
 * Trả về logs, status, session state + các hàm start/stop/clearLogs.
 */
export function useLivestreamSession(accountId: string | undefined) {
  const [logs, setLogs] = React.useState<LivestreamLogEntry[]>([]);
  const [status, setStatus] = React.useState<StreamStatus>('idle');
  const [session, setSession] = React.useState<SessionState>(INITIAL_SESSION);

  const eventSourceRef = React.useRef<EventSource | null>(null);
  const parseSessionDataRef = React.useRef<((entry: LivestreamLogEntry) => void) | null>(null);
  const retryTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = React.useRef(0);
  // true khi user còn muốn xem stream này (giữa startStream() và stopStream()/unmount) —
  // TL bắt tay 2 lớp có thể lâu hơn vài giây, lỗi thoáng qua không nên bỏ cuộc vĩnh viễn.
  const wantStreamRef = React.useRef(false);

  const addLog = React.useCallback((entry: LivestreamLogEntry) => {
    setLogs((prev) => [...prev, entry]);
  }, []);

  // ─── Countdown effect ─────────────────────────────────────────────
  React.useEffect(() => {
    const timer = setInterval(() => {
      setSession((prev) => {
        if (prev.status === 'BETTING' && prev.countdown > 0) {
          const newCountdown = prev.countdown - 1;
          if (newCountdown <= prev.stopBetSecond && newCountdown > 0) {
            return { ...prev, countdown: newCountdown, status: 'WAITING' };
          }
          if (newCountdown === 0) {
            return { ...prev, countdown: 0, status: 'WAITING' };
          }
          return { ...prev, countdown: newCountdown };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // ─── Parse SSE message for session data ───────────────────────────
  const parseSessionData = React.useCallback((entry: LivestreamLogEntry) => {
    if (entry.direction !== 'RECV' || !entry.raw) return;

    const raw = entry.raw as unknown[];
    if (!Array.isArray(raw) || raw[0] !== 5 || typeof raw[1] !== 'object') return;

    const payload = raw[1] as Record<string, unknown>;
    const cmd = payload.cmd;

    // cmd 1950: Full session data
    if (cmd === 1950) {
      const sessionId = payload.sessionId as number;
      const timeBetCountdown = payload.timeBetCountdown as number | undefined;
      const timeBet = (payload.timeBet as number) ?? 35;
      const sessionStatus = payload.status as string;
      const stopBetSecond = (payload.stopBetSecond as number) ?? 3;
      const coins = (payload.coins as number[]) ?? [];
      const jackpotAmount = payload.jackpotAmount as number;
      const dealerName = payload.dealerName as string;
      const odds = payload.odds as Array<{ name: string; rate: number }>;
      const htr = (payload.htr as Array<{ resultRaw: string; sessionId: number; st: number }>) ?? [];

      const history: HistoryEntry[] = htr.map((item) => ({
        sessionId: item.sessionId,
        resultRaw: item.resultRaw,
        result: parseResult(item.resultRaw),
      }));

      const lastResult = history.length > 0 ? history[0]!.result : null;
      const endBettingTime = payload.endBettingTime as number | undefined;

      const seconds = sessionStatus === 'BETTING'
        ? (timeBetCountdown ?? timeBet ?? 35)
        : 0;

      setSession((prev) => ({
        ...prev,
        sessionId,
        timeBet,
        endBettingTime: endBettingTime ?? null,
        stopBetSecond,
        status: sessionStatus,
        coins: coins.length > 0 ? coins : prev.coins,
        jackpot: jackpotAmount ?? prev.jackpot,
        dealerName: dealerName ?? prev.dealerName,
        odds: odds ?? prev.odds,
        lastResult,
        history,
        countdown: seconds,
      }));
    }

    // cmd 1955: Bắt đầu phiên mới (BETTING)
    if (cmd === 1955) {
      const sessionId = payload.sessionId as number;
      const timeBetCountdown = payload.timeBetCountdown as number | undefined;
      const timeBet = (payload.timeBet as number) ?? 35;
      const stopBetSecond = (payload.stopBetSecond as number) ?? 3;
      const dealerName = payload.dealerName as string;
      const odds = payload.odds as Array<{ name: string; rate: number }>;
      const jackpotAmount = payload.jackpotAmount as number;
      const endBettingTime = payload.endBettingTime as number | undefined;

      const seconds = timeBetCountdown ?? timeBet ?? 35;

      setSession((prev) => ({
        ...prev,
        sessionId,
        timeBet,
        endBettingTime: endBettingTime ?? null,
        stopBetSecond,
        status: 'BETTING',
        dealerName: dealerName ?? prev.dealerName,
        odds: odds ?? prev.odds,
        jackpot: jackpotAmount ?? prev.jackpot,
        betSummary: null,
        totalPlayers: null,
        betTypeResult: null,
        countdown: seconds,
      }));
    }

    // cmd 1962: Chờ kết quả (ROLLING)
    if (cmd === 1962) {
      setSession((prev) => ({
        ...prev,
        status: 'ROLLING',
        endBettingTime: null,
        countdown: 0,
      }));
    }

    // cmd 1956: Kết thúc phiên + Kết quả (ENDED)
    if (cmd === 1956) {
      const resultRaw = payload.resultRaw as string;
      const betTypeResult = payload.betTypeResult as string;
      const sessionId = payload.sessionId as number;
      const jackpotAmount = payload.jackpotAmount as number;

      if (resultRaw) {
        const result = parseResult(resultRaw);
        const historyEntry: HistoryEntry = { sessionId, result, resultRaw };

        setSession((prev) => {
          const exists = prev.history.some((h) => h.sessionId === sessionId);
          const history = exists
            ? prev.history
            : [historyEntry, ...prev.history].slice(0, 50);
          return {
            ...prev,
            status: 'ENDED',
            endBettingTime: null,
            countdown: 0,
            lastResult: result,
            betTypeResult: betTypeResult ?? null,
            history,
            jackpot: jackpotAmount ?? prev.jackpot,
          };
        });
      }
    }

    // cmd 10000: Jackpot realtime update
    if (cmd === 10000) {
      const js = payload.Js as Array<{ gid: number; ba: number }>;
      if (js) {
        const txJackpot = js.find((j) => j.gid === 305 || j.gid === 306);
        if (txJackpot) {
          setSession((prev) => ({ ...prev, jackpot: txJackpot.ba }));
        }
      }
    }
  }, []);

  React.useEffect(() => {
    parseSessionDataRef.current = parseSessionData;
  }, [parseSessionData]);

  // ─── Connect (dùng cả cho lần đầu lẫn retry) ────────────────────────
  const connect = React.useCallback(() => {
    if (!accountId) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setStatus('connecting');

    const url = `${siteConfig.apiUrl}/api/v1/sunwin/livestream/${accountId}/stream`;
    const es = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as LivestreamLogEntry;
        retryCountRef.current = 0;
        addLog(data);
        parseSessionDataRef.current?.(data);

        if (data.cmd === 'DONE') {
          setStatus('running');
        } else if (data.cmd === 'FAILED' || data.direction === 'ERROR') {
          setStatus('failed');
        } else if (data.cmd === 'AUTH_SUCCESS' || data.cmd === 'PING_START') {
          setStatus('running');
        }
      } catch (err) {
        console.error('Failed to parse SSE data:', err);
      }
    };

    // Retry với backoff thay vì bỏ cuộc ngay ở lỗi đầu tiên — server có thể vẫn đang
    // start (Tứ Linh bắt tay 2 lớp lâu hơn vài giây), 400 lúc này là tạm thời, không
    // phải "chưa từng start". Chỉ dừng hẳn khi user tự stopStream()/đóng modal.
    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;

      let alreadyDone = false;
      setStatus((prev) => {
        if (prev === 'done') alreadyDone = true;
        return prev;
      });
      if (alreadyDone || !wantStreamRef.current) return;

      addLog({
        direction: 'ERROR',
        timestamp: new Date().toISOString(),
        cmd: 'CONNECTION_ERROR',
        label: 'Mất kết nối với máy chủ API — đang thử lại...',
        raw: {},
      });
      setStatus('failed');

      const delay = Math.min(1000 * 2 ** retryCountRef.current, 10000);
      retryCountRef.current += 1;
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        if (wantStreamRef.current) connect();
      }, delay);
    };
  }, [accountId, addLog]);

  // ─── Start SSE stream ─────────────────────────────────────────────
  const startStream = React.useCallback(() => {
    if (!accountId) return;
    wantStreamRef.current = true;
    retryCountRef.current = 0;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setLogs([]);
    connect();
  }, [accountId, connect]);

  // ─── Stop SSE stream ──────────────────────────────────────────────
  const stopStream = React.useCallback(() => {
    wantStreamRef.current = false;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const clearLogs = React.useCallback(() => {
    setLogs([]);
  }, []);

  // ─── Cleanup on unmount ───────────────────────────────────────────
  React.useEffect(() => {
    return () => {
      wantStreamRef.current = false;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  return {
    logs,
    status,
    session,
    startStream,
    stopStream,
    clearLogs,
  };
}